import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdditionalContacts from '../../components/crm/AdditionalContacts';
import { AdminQuotes } from '../../components/crm/Quotes';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CommunicationSection from '../../components/admin/CommunicationSection';
import SpaceIntakeSection from '../../components/admin/SpaceIntakeSection';
import { EmailActivity } from '../../components/admin/email/EmailActivity';
import UserAvatar from '../../components/UserAvatar';
import {
  ArrowLeft, Loader2, Mail, Phone, MapPin, Tag, DollarSign, Briefcase, MessageSquare,
  CreditCard, Star, Pencil, Plus, ExternalLink, Sparkles, CalendarDays, Clock, FileText,
  Home, User as UserIcon, ClipboardList
} from 'lucide-react';
import { EditCustomerDialog, AddressEditDialog } from './AdminClientsPage';
import AddNewCleanDialog from '../../components/admin/AddNewCleanDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmptyEnum = { lead_sources: [], client_types: [], contact_methods: [], cleaning_frequencies: [], payment_methods: [], payment_statuses: [] };

const money = v => `$${Number(v || 0).toFixed(2)}`;
const dateLine = v => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const dateShort = v => (v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const InfoRow = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-2.5 py-1.5">
    {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5 flex-shrink-0" />}
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-semibold text-zinc-900 truncate ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  </div>
);

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-3">
    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">{label}</p>
    <p className={`text-xl font-extrabold mt-1 ${accent || 'text-zinc-900'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
  </div>
);

const SectionCard = ({ icon: Icon, title, action, children, testId }) => (
  <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-3.5 border-b border-zinc-100 bg-zinc-50/50 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-[#F56F05] flex-shrink-0" />}
        <p className="text-sm font-bold text-zinc-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</p>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const IntakeCardWrapper = ({ action, children }) => (
  <SectionCard icon={ClipboardList} title="Space Intake" action={action} testId="space-intake-card">{children}</SectionCard>
);

const AdminCustomerDetailPage = () => {
  const { userId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/customers');
  const [searchParams] = useSearchParams();
  const prefillDate = searchParams.get('prefill_date');
  const intakeDeepLink = searchParams.get('intake');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData] = useState(null);
  const [enums, setEnums] = useState(EmptyEnum);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [addressDialog, setAddressDialog] = useState(null);
  const [addCleanOpen, setAddCleanOpen] = useState(false);
  const [bookQuote, setBookQuote] = useState(null);
  useEffect(() => { if (prefillDate) setAddCleanOpen(true); }, [prefillDate]);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const fetchAll = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [dRes, eRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/crm/clients/${userId}/full`, { headers }),
        axios.get(`${API_URL}/api/admin/crm/enums`, { headers }).catch(() => ({ data: EmptyEnum })),
      ]);
      setData(dRes.data);
      setEnums(eRes.data);
    } catch {
      toast.error('Failed to load customer profile');
    } finally {
      setLoading(false);
    }
  }, [userId, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API_URL}/api/admin/crm/clients/${userId}/notes`, { body: noteBody }, { headers });
      setNoteBody('');
      toast.success('Note added');
      fetchAll();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleRequestCard = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/crm/clients/${userId}/request-card`, {}, { headers });
      toast.success('Card capture link sent');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send link');
    }
  };

  const handleSetPrimary = async (addressId) => {
    try {
      await axios.post(`${API_URL}/api/admin/crm/addresses/${addressId}/set-primary`, {}, { headers });
      toast.success('Set as primary');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address? Any linked appointments will need to be reassigned.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/crm/addresses/${addressId}`, { headers });
      toast.success('Address deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-zinc-500 mb-4">Customer not found.</p>
        <Button onClick={back}>Back to Customers</Button>
      </div>
    );
  }

  const p = { ...(data.profile || {}), user_id: userId };
  const summary = data.summary || {};
  const primaryAddress = (data.addresses || []).find(a => a.is_primary) || (data.addresses || [])[0];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-customer-detail-page">
      {/* Page Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-12 md:top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="flex items-start sm:items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="icon" onClick={back} data-testid="back-customers-btn" className="flex-shrink-0 sm:w-auto sm:px-3">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline text-sm">Customers</span>
            </Button>
            <div className="hidden sm:block h-6 w-px bg-zinc-200" />
            <UserAvatar user={{ full_name: p.user_name, photo_url: p.user_photo_url, role: 'client' }} size="md" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h1 className="text-base sm:text-xl font-extrabold text-zinc-900 truncate max-w-full" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.user_name || 'Unnamed customer'}</h1>
                {p.is_manual && <Badge variant="outline" className="bg-purple-50 text-purple-700 text-[10px]">Concierge</Badge>}
                {p.lead_source && <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]"><Tag className="w-3 h-3 mr-1" />{p.lead_source}</Badge>}
                {p.client_type && <Badge variant="outline" className="text-[10px]">{p.client_type}</Badge>}
              </div>
              <p className="text-[11px] sm:text-xs text-zinc-500 truncate">{p.user_email} · {p.user_phone || 'no phone'}</p>
            </div>
            {/* Desktop actions */}
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              <Button size="lg" onClick={() => setAddCleanOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold" data-testid="add-new-clean-btn">
                <Plus className="w-4 h-4 mr-2" /> Add New Clean
              </Button>
              {!p.has_payment_method && (
                <Button size="sm" variant="outline" onClick={handleRequestCard} data-testid="request-card-btn">
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Request card
                </Button>
              )}
              <Button
                size="lg"
                onClick={() => setEditOpen(true)}
                className="bg-[#F56F05] hover:bg-[#C2410C] text-white shadow-lg shadow-[#F56F05]/30 font-bold"
                data-testid="edit-customer-btn"
              >
                <Pencil className="w-4 h-4 mr-2" /> Edit Customer Info
              </Button>
            </div>
            {/* Mobile edit icon */}
            <Button
              size="icon"
              onClick={() => setEditOpen(true)}
              className="sm:hidden bg-[#F56F05] hover:bg-[#C2410C] text-white flex-shrink-0"
              data-testid="edit-customer-btn-mobile"
              aria-label="Edit customer"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </div>
          {/* Mobile primary action */}
          <Button onClick={() => setAddCleanOpen(true)} className="sm:hidden w-full mt-3 bg-zinc-900 hover:bg-zinc-800 text-white font-bold" data-testid="add-new-clean-btn-mobile">
            <Plus className="w-4 h-4 mr-2" /> Add New Clean
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Jobs" value={summary.total_appointments || 0} />
          <StatCard label="Completed" value={summary.completed_count || 0} />
          <StatCard label="Lifetime revenue" value={money(summary.total_revenue_paid || 0)} accent="text-[#F56F05]" />
          <StatCard label="Unpaid" value={money(summary.unpaid_balance || 0)} accent={(summary.unpaid_balance || 0) > 0 ? 'text-red-600' : 'text-zinc-900'} />
        </div>

        {!p.has_payment_method && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-6 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 flex items-center gap-3"
            data-testid="no-card-banner"
          >
            <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center text-white shadow"><CreditCard className="w-4 h-4" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-zinc-900">No card on file</p>
              <p className="text-xs text-zinc-600">Send a secure card-capture link so you can charge invoices without asking again.</p>
            </div>
            <Button size="sm" onClick={handleRequestCard}>Send link</Button>
          </motion.div>
        )}

        {prefillDate && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-2xl border-2 border-[#F56F05] bg-orange-50 p-4 flex items-center gap-3"
            data-testid="prefill-date-banner"
          >
            <div className="w-9 h-9 rounded-lg bg-[#F56F05] flex items-center justify-center text-white shadow">
              <CalendarDays className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zinc-900">Booking for {new Date(prefillDate).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
              <p className="text-xs text-zinc-700">Use the &ldquo;Log past clean&rdquo; or &ldquo;Add appointment&rdquo; flow below with this date preselected.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/customers/${userId}`)} data-testid="dismiss-prefill-btn">
              Dismiss
            </Button>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: Main content — 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            <SectionCard
              icon={UserIcon}
              title="Profile & Contact"
              testId="profile-card"
              action={
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#F56F05]/30 text-[#F56F05] hover:bg-orange-50 hover:text-[#F56F05]"
                  onClick={() => setEditOpen(true)}
                  data-testid="profile-edit-btn"
                >
                  <Pencil className="w-3 h-3 mr-1.5" /> Edit all fields
                </Button>
              }
            >
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                <InfoRow icon={UserIcon} label="Full name" value={p.user_name} />
                <InfoRow icon={Mail} label="Email" value={p.user_email && !p.user_email.includes('@gleamgurus.internal') ? p.user_email : '(placeholder)'} />
                <InfoRow icon={Phone} label="Phone" value={p.user_phone} />
                <InfoRow icon={MessageSquare} label="Preferred contact" value={p.preferred_contact_method} />
                <InfoRow icon={Tag} label="Lead source" value={p.lead_source} />
                <InfoRow icon={Briefcase} label="Customer type" value={p.client_type} />
                <InfoRow icon={Clock} label="Cleaning frequency" value={p.cleaning_frequency} />
                <InfoRow icon={DollarSign} label="Agreed price" value={p.agreed_price ? money(p.agreed_price) : '—'} />
              </div>
              {(p.lead_source_notes || p.cleaning_preferences || p.special_instructions) && (
                <div className="mt-4 pt-4 border-t border-zinc-100 space-y-2">
                  {p.lead_source_notes && <p className="text-xs text-zinc-600"><span className="font-bold text-zinc-500 uppercase tracking-wider mr-1">Lead notes:</span>{p.lead_source_notes}</p>}
                  {p.cleaning_preferences && <p className="text-xs text-zinc-600"><span className="font-bold text-zinc-500 uppercase tracking-wider mr-1">Preferences:</span>{p.cleaning_preferences}</p>}
                  {p.special_instructions && <p className="text-xs text-zinc-600"><span className="font-bold text-zinc-500 uppercase tracking-wider mr-1">Special:</span>{p.special_instructions}</p>}
                </div>
              )}
              {(p.property_manager_name || p.property_manager_phone || p.property_manager_email) && (
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Property Manager</p>
                  <div className="grid sm:grid-cols-3 gap-2 text-xs text-zinc-700">
                    <div>{p.property_manager_name || '—'}</div>
                    <div>{p.property_manager_phone || '—'}</div>
                    <div className="truncate">{p.property_manager_email || '—'}</div>
                  </div>
                </div>
              )}
            </SectionCard>

            <SectionCard
              icon={MapPin} title="Addresses" testId="addresses-card"
              action={<Button size="sm" variant="outline" onClick={() => setAddressDialog({ mode: 'add' })} data-testid="add-address-btn"><Plus className="w-3 h-3 mr-1" /> Add address</Button>}
            >
              {(data.addresses || []).length === 0 ? (
                <p className="text-xs text-zinc-500 italic py-2">No addresses recorded.</p>
              ) : (
                <div className="space-y-2">
                  {(data.addresses || []).map(a => (
                    <div key={a.id} className="rounded-xl bg-zinc-50 p-3 flex items-start justify-between gap-2" data-testid={`address-row-${a.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-semibold text-sm text-zinc-900">{a.line1}{a.line2 ? `, ${a.line2}` : ''}</p>
                          {a.is_primary && <Badge className="bg-[#F56F05] text-white text-[9px]">Primary</Badge>}
                          {a.label && <Badge variant="outline" className="text-[9px]">{a.label}</Badge>}
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">{a.city}, {a.state} {a.zip}</p>
                        {a.access_notes && <p className="text-[11px] text-zinc-400 mt-1">Access: {a.access_notes}</p>}
                        {(a.gate_code || a.lockbox_code) && <p className="text-[11px] text-zinc-400">Gate: {a.gate_code || '—'} · Lockbox: {a.lockbox_code || '—'}</p>}
                        {a.pet_notes && <p className="text-[11px] text-zinc-400">Pets: {a.pet_notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!a.is_primary && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Set primary" onClick={() => handleSetPrimary(a.id)} data-testid={`set-primary-${a.id}`}>
                            <Star className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit" onClick={() => setAddressDialog({ mode: 'edit', address: a })} data-testid={`edit-address-${a.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" title="Delete" onClick={() => handleDeleteAddress(a.id)} data-testid={`delete-address-${a.id}`}>
                          <FileText className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <AdditionalContacts baseUrl={`/api/admin/clients/${userId}/contacts`} headers={headers} properties={data?.addresses || []} />
            <AdminQuotes clientId={userId} headers={headers} properties={data?.addresses || []} onBook={(q) => { setBookQuote(q); setAddCleanOpen(true); }} />

            <SpaceIntakeSection customerUserId={userId} headers={headers} autoOpenId={intakeDeepLink} wrapper={IntakeCardWrapper} />
            <EmailActivity customerId={userId} headers={headers} />

            <SectionCard icon={CalendarDays} title="Appointments & History" testId="history-card">
              <Tabs defaultValue="upcoming">
                <TabsList className="mb-3">
                  <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({(data.appointments || []).filter(a => new Date(a.start_time) >= new Date()).length})</TabsTrigger>
                  <TabsTrigger value="past" data-testid="tab-past">Past ({(data.appointments || []).filter(a => new Date(a.start_time) < new Date()).length})</TabsTrigger>
                </TabsList>
                {['upcoming', 'past'].map(bucket => (
                  <TabsContent key={bucket} value={bucket}>
                    {(() => {
                      const rows = (data.appointments || []).filter(a => bucket === 'upcoming' ? new Date(a.start_time) >= new Date() : new Date(a.start_time) < new Date());
                      if (rows.length === 0) return <p className="text-xs text-zinc-500 italic py-3">No {bucket} appointments.</p>;
                      return (
                        <div className="space-y-1.5">
                          {rows.slice(0, 10).map(a => (
                            <Link key={a.id} to={`/admin/appointments/${a.id}`} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-zinc-50 border border-zinc-100">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-zinc-900">{dateShort(a.start_time)} · {a.service?.name || 'Service'}</p>
                                <p className="text-xs text-zinc-500 truncate">
                                  {new Date(a.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                  {' · '}
                                  {a.cleaner_name ? (
                                    <>
                                      <span className="font-medium text-zinc-700">{a.cleaner_name}</span>
                                      {a.team_count > 1 && (
                                        <span className="ml-1 text-[10px] font-semibold text-[#F56F05]">+{a.team_count - 1} more</span>
                                      )}
                                    </>
                                  ) : 'Unassigned'}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-[10px] flex-shrink-0 ml-2">{a.status}</Badge>
                            </Link>
                          ))}
                        </div>
                      );
                    })()}
                  </TabsContent>
                ))}
              </Tabs>
            </SectionCard>

            <CommunicationSection
              userId={p.user_id}
              customerEmail={p.email}
              preferredContact={p.preferred_contact}
              headers={headers}
            />

            <SectionCard icon={FileText} title="Invoices" testId="invoices-card">
              {(data.invoices || []).length === 0 ? (
                <p className="text-xs text-zinc-500 italic py-2">No invoices yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {(data.invoices || []).slice(0, 10).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-100 hover:bg-zinc-50" data-testid={`invoice-row-${inv.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-900">#{inv.number || inv.id.slice(0, 8)} · {money(inv.total)}</p>
                        <p className="text-xs text-zinc-500">{dateShort(inv.issued_at || inv.created_at)}</p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] ${
                        inv.status === 'Paid' ? 'bg-emerald-50 text-emerald-700' :
                        inv.status === 'Overdue' ? 'bg-red-50 text-red-700' :
                        inv.status === 'Failed' ? 'bg-red-100 text-red-800' :
                        'bg-zinc-50 text-zinc-700'
                      }`}>{inv.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard icon={MessageSquare} title="Notes & Timeline" testId="notes-card">
              <div className="mb-4">
                <Textarea
                  rows={2}
                  value={noteBody}
                  onChange={e => setNoteBody(e.target.value)}
                  placeholder="Add an internal note — visible to admins and staff only…"
                  className="text-sm"
                  data-testid="new-note-input"
                />
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={handleSaveNote} disabled={savingNote || !noteBody.trim()} data-testid="save-note-btn">
                    {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Add note
                  </Button>
                </div>
              </div>
              {(data.notes || []).length === 0 ? (
                <p className="text-xs text-zinc-500 italic">No notes yet.</p>
              ) : (
                <div className="space-y-2">
                  {(data.notes || []).slice(0, 12).map(n => (
                    <div key={n.id} className="rounded-lg bg-zinc-50 p-2.5 text-sm text-zinc-800" data-testid={`note-${n.id}`}>
                      <p>{n.body}</p>
                      <p className="text-[10px] text-zinc-500 mt-1">{n.author_name || 'Staff'} · {dateLine(n.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* RIGHT: Sidebar */}
          <div className="space-y-6">
            <SectionCard icon={CreditCard} title="Card on File" testId="card-card">
              {p.has_payment_method ? (
                <div className="text-sm">
                  <p className="font-semibold text-zinc-900">•••• {p.card_last4 || '••••'}</p>
                  <p className="text-xs text-zinc-500 mt-1">{p.card_brand || 'Card'} · Ready for auto-charge</p>
                </div>
              ) : (
                <div className="text-sm">
                  <p className="text-zinc-600 mb-2">No card on file yet.</p>
                  <Button size="sm" onClick={handleRequestCard} className="w-full">Send capture link</Button>
                </div>
              )}
            </SectionCard>

            <SectionCard icon={Home} title="Primary Address" testId="primary-address-card">
              {primaryAddress ? (
                <div className="text-sm">
                  <p className="font-semibold text-zinc-900">{primaryAddress.line1}{primaryAddress.line2 ? `, ${primaryAddress.line2}` : ''}</p>
                  <p className="text-xs text-zinc-500">{primaryAddress.city}, {primaryAddress.state} {primaryAddress.zip}</p>
                </div>
              ) : (
                <p className="text-xs text-zinc-500 italic">No primary address.</p>
              )}
            </SectionCard>

            <SectionCard icon={Sparkles} title="Attribution" testId="attribution-card">
              <div className="text-sm space-y-1.5">
                <p><span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block">Lead source</span>{p.lead_source || '—'}</p>
                {p.lead_source_notes && <p><span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block">Notes</span>{p.lead_source_notes}</p>}
                <p><span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block">Customer since</span>{dateShort(p.created_at)}</p>
              </div>
            </SectionCard>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4">
              <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider font-bold">Danger zone</p>
              <Link
                to={`/admin/users/${p.user_id}`}
                className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-[#F56F05]"
                data-testid="open-user-record-link"
              >
                Open raw user record <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {editOpen && (
        <EditCustomerDialog
          customer={p}
          enums={enums}
          headers={headers}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchAll(); toast.success('Customer updated'); }}
        />
      )}
      <AddNewCleanDialog
        open={addCleanOpen}
        onOpenChange={(v) => { setAddCleanOpen(v); if (!v) setBookQuote(null); }}
        headers={headers}
        defaultStart={prefillDate || null}
        defaultClient={{ user_id: userId, user_name: p.user_name, user_email: p.user_email }}
        fromQuote={bookQuote}
        onCreated={() => fetchAll(true)}
      />
      {addressDialog && (
        <AddressEditDialog
          mode={addressDialog.mode}
          userId={userId}
          address={addressDialog.address}
          headers={headers}
          onClose={() => setAddressDialog(null)}
          onSaved={() => { setAddressDialog(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

export default AdminCustomerDetailPage;
